import React, {useCallback, useEffect, useState} from 'react';
import PaginationArrowLeftIcon from '../../../../assets/icons/PaginationArrowLeft.svg?react';
import PaginationArrowRightIcon from '../../../../assets/icons/PaginationArrowRight.svg?react';
import PaginationArrowLeftDisabledIcon from '../../../../assets/icons/PaginationArrowLeftDisabled.svg?react';
import PaginationArrowRightDisabledIcon from '../../../../assets/icons/PaginationArrowRightDisabled.svg?react';

interface PaginationProps {
    numberOfPages: number;
    page?: number;
    onPageClick: (clickedPage: number) => void;
}

function Pagination(props: PaginationProps): JSX.Element | null {
    const {numberOfPages, page = 1, onPageClick} = props;

    const maxSlots = 7;

    const paginationStartNumber = useCallback(
        (selectedPage: number): number => {
            const slotBeforeDots = maxSlots - 4;

            // selecting slot before three dots (1, 2, 3, 4, ..., 8, 9 -> selected slot: 4) scrolls pagination by 1 (2, 3, 4, 5, ..., 8, 9)
            // slot with selected value (4) moves from 4. place to 3. place (now 3. slot is selected because of scrolling)
            const selectedSlotAfterScrolling = slotBeforeDots - 1;

            // range from 1 till number before three dots (1, 2, 3, 4, ..., 8, 9 -> range: 1, 2, 3)
            // selecting these numbers doesnt scroll pagination (pagination starts at 1)
            const noScrollingRange = selectedPage >= 1 && selectedPage < slotBeforeDots;

            // range of numbers that does scroll pagination (1, 2, 3, 4, ..., 8, 9 -> range: 4, 5)
            // selecting any of these numbers changes pagination starting number accordingly -> it scrolls pagination
            const scrollRange = selectedPage >= slotBeforeDots && selectedPage <= numberOfPages - 4;

            if (noScrollingRange) {
                return 1;
            }
            if (scrollRange) {
                return selectedPage - selectedSlotAfterScrolling;
            }

            // if range above dotRange is selected (1, 2, 3, 4, ..., 8, 9 -> range: 6, 7, 8, 9) pagination doesnt scroll
            // because sequence till the last number is visible (3, 4, 5, 6, 7, 8, 9) so there is no need for scrolling
            return numberOfPages - (maxSlots - 1);
        },
        [maxSlots, numberOfPages]
    );

    const [startingNumber, setStartingNumber] = useState<number>(paginationStartNumber(page));
    const [selectedPage, setSelectedPage] = useState<number>(page);
    const [allowScrollUp, setAllowScrollUp] = useState<boolean>(true);
    const [allowScrollDown, setAllowScrollDown] = useState<boolean>(true);

    const beforeThreeDotsSlot = startingNumber + (maxSlots - 4);

    useEffect(() => {
        setSelectedPage(page);
        setStartingNumber(paginationStartNumber(page));
    }, [page, paginationStartNumber]);

    function onClick(clickedPage: number): void {
        const scrollUp = clickedPage === beforeThreeDotsSlot && allowScrollUp;
        const scrollDown = clickedPage === startingNumber && allowScrollDown;
        const lastTwoNumbers = clickedPage === numberOfPages || clickedPage === numberOfPages - 1;

        if (numberOfPages > maxSlots) {
            if (scrollUp) {
                setStartingNumber(startingNumber + 1);
            } else if (scrollDown) {
                setStartingNumber(startingNumber - 1);
            } else if (lastTwoNumbers) {
                setStartingNumber(numberOfPages - (maxSlots - 1));
            }
        }

        setSelectedPage(clickedPage);
        onPageClick(clickedPage);
    }

    function enableOrDisableScroll(): void {
        const sequenceTillLast = startingNumber + maxSlots - 1 === numberOfPages;
        const sequenceTillThreeDots = startingNumber + maxSlots - 1 !== numberOfPages;

        if (startingNumber === 1 && allowScrollDown) setAllowScrollDown(false);
        if (startingNumber > 1 && !allowScrollDown) setAllowScrollDown(true);

        if (sequenceTillLast && allowScrollUp) setAllowScrollUp(false);
        if (sequenceTillThreeDots && !allowScrollUp) setAllowScrollUp(true);
    }

    function renderSlotsWithDots(startingNumber: number, numberOfPages: number): JSX.Element[] {
        const sequenceTillLast = startingNumber + maxSlots - 1 === numberOfPages;

        const slotsValues: (number | string)[] = Array.from({length: maxSlots}, (_, index) => index + startingNumber);

        slotsValues[maxSlots - 1] = numberOfPages;
        slotsValues[maxSlots - 2] = numberOfPages - 1;

        if (!sequenceTillLast) slotsValues[maxSlots - 3] = '...';

        enableOrDisableScroll();

        return slotsValues.map((slotValue, index) => {
            return (
                <div
                    key={index}
                    className={`c-pagination__box ${slotValue === selectedPage && 'c-pagination__box--selected'}`}
                    onClick={() => Number.isInteger(slotValue) && onClick(slotValue as number)}
                >
                    {slotValue}
                </div>
            );
        });
    }

    function renderSlotsWithoutDots(numberOfPages: number): JSX.Element[] {
        return Array.from({length: numberOfPages}, (_, index) => index + 1).map((slotValue, index) => {
            return (
                <div
                    key={index}
                    className={`c-pagination__box ${slotValue === selectedPage && 'c-pagination__box--selected'}`}
                    onClick={() => onClick(slotValue)}
                >
                    {slotValue}
                </div>
            );
        });
    }

    function handleArrowUpClick(): void {
        const scrollUp = selectedPage === beforeThreeDotsSlot - 1;

        if (scrollUp && allowScrollUp) {
            setStartingNumber(startingNumber + 1);
        }
        if (selectedPage < numberOfPages) {
            setSelectedPage(selectedPage + 1);
            onPageClick(selectedPage + 1);
        }
    }

    function handleArrowDownClick(): void {
        const scrollDown = selectedPage === startingNumber || selectedPage === startingNumber + 1;

        if (scrollDown && allowScrollDown) {
            setStartingNumber(startingNumber - 1);
        }
        if (selectedPage > 1) {
            setSelectedPage(selectedPage - 1);
            onPageClick(selectedPage - 1);
        }
    }

    if (numberOfPages <= 1 || page < 1 || page > numberOfPages) {
        return null;
    }

    return (
        <div className="c-pagination">
            <div className="c-pagination__box" onClick={() => handleArrowDownClick()}>
                {selectedPage === 1 ? <PaginationArrowLeftDisabledIcon /> : <PaginationArrowLeftIcon />}
            </div>
            {numberOfPages > maxSlots ? renderSlotsWithDots(startingNumber, numberOfPages) : renderSlotsWithoutDots(numberOfPages)}
            <div className="c-pagination__box" onClick={() => handleArrowUpClick()}>
                {selectedPage === numberOfPages ? <PaginationArrowRightDisabledIcon /> : <PaginationArrowRightIcon />}
            </div>
        </div>
    );
}

export default Pagination;
